import { lazy } from "react";
import { BC_HQ, INVENTORY, PARTNERS } from "app_constants";
import { ICON_TYPE } from "components/icons/icon";

import { DateRangeSupport } from "helpers/shared/formatters/dates";
import {
  userListsPermission,
  userRolesAnalyticsPermission,
  userRolesBCPermission,
  userUpdatePermission,
} from "navigation/permissions";
import { CreateUserButton } from "pos_users/components/CreateUserButton";

import type { ISectionProps } from "types/navigation";

const USERS_ROLES: ISectionProps = {
  feature: "Users & Roles",
  icon: ICON_TYPE.USERS,
  path: "/users",
  level: ["brand"],
  dateSupport: DateRangeSupport.NONE,
  requiredSessions: ["partners", "breadcrumb", "inventory"],
  subSections: [
    {
      feature: "Users",
      level: ["brand"],
      scope: ["staff", "superuser"],
      path: "/users/browse",
      dateSupport: DateRangeSupport.NONE,
      tabNavItems: [
        {
          feature: "All Users",
          component: lazy(() => import("containers/users/all_users_container")),
          level: ["brand"],
          scope: ["staff", "superuser"],
          path: "/users/browse/all",
          dateSupport: DateRangeSupport.NONE,
          requiredSessions: ["partners", "breadcrumb", "inventory"],
          verifyPermissions: userListsPermission,
          actionButtonsComponent: CreateUserButton,
        },
        {
          feature: "POS Only Users",
          component: lazy(() => import("containers/users/all_users_container")),
          level: ["brand"],
          scope: ["staff", "superuser"],
          path: "/users/browse/pos",
          dateSupport: DateRangeSupport.NONE,
          requiredSessions: ["breadcrumb"],
          verifyPermissions: userListsPermission,
          actionButtonsComponent: CreateUserButton,
        },
      ],
    },
    {
      feature: "Edit User",
      hideInNav: true,
      level: ["brand"],
      scope: ["staff", "superuser"],
      path: "/users/edit",
      dateSupport: DateRangeSupport.NONE,
      tabNavItems: [
        {
          feature: "General",
          component: lazy(() => import("containers/users/edit_user_container")),
          level: ["brand"],
          scope: ["staff", "superuser"],
          path: "/users/edit/general",
          dateSupport: DateRangeSupport.NONE,
          requiredSessions: ["partners", "breadcrumb", "inventory"],
        },
        {
          feature: "Analytics",
          app: PARTNERS,
          level: ["brand"],
          scope: ["staff", "superuser"],
          path: "/users/edit/analytics",
          component: lazy(
            () => import("containers/users/enable/enable_analytics_container")
          ),
          legacyPath: ({ id }) => `/settings/users/${id}`,
          dateSupport: DateRangeSupport.NONE,
          requiredSessions: ["partners", "breadcrumb", "inventory"],
        },
        {
          feature: "Point of Sale",
          app: BC_HQ,
          level: ["store"],
          scope: ["staff", "superuser"],
          path: "/users/edit/pos",
          component: lazy(
            () => import("containers/users/enable/enable_pos_container")
          ),
          legacyPath: ({ id }) => `/pos_users/${id}/edit`,
          dateSupport: DateRangeSupport.NONE,
          requiredSessions: ["partners", "breadcrumb", "inventory"],
        },
        {
          feature: "Inventory",
          app: INVENTORY,
          level: [],
          scope: ["staff", "superuser"],
          path: "/users/edit/inventory",
          component: lazy(
            () => import("containers/users/enable/enable_inventory_container")
          ),
          legacyPath: () => "/settings/#manage_users", // todo: some way to target a user directly?
          dateSupport: DateRangeSupport.NONE,
          requiredSessions: ["partners", "breadcrumb", "inventory"],
        },
      ],
    },
    {
      feature: "Add New User",
      hideInNav: true,
      level: ["store"],
      scope: ["staff", "superuser"],
      path: "/users/create",
      dateSupport: DateRangeSupport.NONE,
      component: lazy(() => import("containers/users/create_user_container")),
      tabNavItems: [],
      verifyPermissions: userUpdatePermission,
    },
    {
      feature: "Create User",
      hideInNav: true,
      level: ["brand"],
      scope: ["staff", "superuser"],
      path: "/users/create-hq",
      dateSupport: DateRangeSupport.NONE,
      component: lazy(
        () => import("containers/users/create_hq_user_container")
      ),
      tabNavItems: [],
    },
    {
      feature: "Roles",
      level: ["store", "brand"],
      scope: ["staff", "superuser"],
      path: "/users/roles",
      dateSupport: DateRangeSupport.NONE,
      tabNavItems: [
        {
          feature: "Analytics",
          app: PARTNERS,
          level: ["brand"],
          scope: ["staff", "superuser"],
          path: "/users/roles/analytics",
          legacyPath: () => "/settings/permissions",
          dateSupport: DateRangeSupport.NONE,
          requiredSessions: ["partners"],
          verifyPermissions: userRolesAnalyticsPermission,
        },
        {
          feature: "Point of Sale",
          app: BC_HQ,
          level: ["store"],
          scope: ["staff", "superuser"],
          path: "/users/roles/pos",
          legacyPath: () => "/roles",
          dateSupport: DateRangeSupport.NONE,
          requiredSessions: ["breadcrumb"],
          verifyPermissions: userRolesBCPermission,
        },
        {
          feature: "Inventory",
          component: lazy(
            () => import("containers/users/roles/inventory_roles_container")
          ),
          level: ["brand"],
          scope: ["staff", "superuser"],
          path: "/users/roles/inventory",
          dateSupport: DateRangeSupport.NONE,
          requiredSessions: ["inventory"],
        },
      ],
    },
  ],
};

export default USERS_ROLES;
